/* eslint-disable no-void */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { noop } from 'lodash';
import { di } from 'react-magnetic-di';
import { usePrevious } from '@atlassian/help-center-common-util/hooks';
import { FORM_SUBMISSION_PROCEED, FORM_SUBMISSION_RE_SUBMIT_AND_PROCEED } from '../constants';
import { useInlineFields, useFailedOrTimedOutInlineFields } from '../controllers/store';
import { useForgeCustomFieldsResolver } from '../controllers/store/forge-custom-fields-resolver';
import type {
    ResolveForgeCustomFields,
    ForgeCustomFieldsResolverStatus,
    ForgeCustomFieldsResolver as ForgeCustomFieldsResolverType,
} from '../types';
import type { FieldState } from 'state/persisted/request-create/types';

export interface Props {
    setResolve: (resolve: ResolveForgeCustomFields) => void;
    setFailedOrTimedOut: (fields: string[]) => void;
    requestTypeId: number;
    fields: FieldState[];
}

export const ForgeCustomFieldsResolver = ({ setResolve, setFailedOrTimedOut, fields, requestTypeId }: Props) => {
    di(useForgeCustomFieldsResolver, useInlineFields, useFailedOrTimedOutInlineFields);

    const [, { resetState }] = useInlineFields();
    const [failedOrTimedOutInlineFields] = useFailedOrTimedOutInlineFields();
    const [resolver, setResolver] = useState<ForgeCustomFieldsResolverType | undefined>();
    const resolverRef = useRef<ForgeCustomFieldsResolverType | undefined>(resolver);
    const resetStateRef = useRef(resetState);
    const prevRequestTypeId = usePrevious(requestTypeId);
    const failedOrTimedOutFieldLabels = useMemo(
        () =>
            failedOrTimedOutInlineFields.map(
                (fieldId) => fields.find((field) => field.id === fieldId)?.label || fieldId
            ),
        [fields, failedOrTimedOutInlineFields]
    );

    useForgeCustomFieldsResolver(resolver);

    const resolve = useCallback(async () => {
        // In case the form was previously re-submitted, the next resolution should take that into account (to avoid looping).
        // We have to be careful here, because the previous promise might have been rejected (no matter what the reason was), thus a separate try/catch block is needed.
        // We also want to void the previous promise to entering this check again.
        try {
            const prevValue = await resolverRef.current?.promise;
            if (prevValue === FORM_SUBMISSION_RE_SUBMIT_AND_PROCEED) {
                setResolver(undefined);
                return FORM_SUBMISSION_PROCEED;
            }
        } catch {
            // nothing to do here
        }

        // Create a new promise and set the resolver to the state.
        // Later on it is used to resolve or reject the promise during the form submission to reflect Forge custom fields status.
        try {
            // Setting default values to avoid TS errors, they are overridden in the next block
            const newResolver: ForgeCustomFieldsResolverType = {
                promise: Promise.resolve(FORM_SUBMISSION_PROCEED),
                resolve: noop,
                reject: noop,
            };
            const promise = new Promise<ForgeCustomFieldsResolverStatus>((res, rej) => {
                newResolver.resolve = res;
                newResolver.reject = rej;
            });
            newResolver.promise = promise;
            setResolver(newResolver);
            return await promise;
        } catch (error) {
            return FORM_SUBMISSION_PROCEED;
        }
    }, []);

    useEffect(() => {
        setResolve(resolve);
    }, [setResolve, resolve]);

    useEffect(() => {
        setFailedOrTimedOut(failedOrTimedOutFieldLabels);
    }, [setFailedOrTimedOut, failedOrTimedOutFieldLabels]);

    useEffect(() => {
        resolverRef.current = resolver;
        resetStateRef.current = resetState;
    }, [resolver, resetState]);

    useEffect(() => {
        if (prevRequestTypeId && prevRequestTypeId !== requestTypeId) {
            void resetState();
        }
    }, [prevRequestTypeId, requestTypeId, resetState]);

    // Reset on unmount, do not add any dependencies to avoid resetting when not needed
    useEffect(() => () => void resetStateRef.current(), []);

    return null;
};
